// frontend/src/features/inventory/pages/MaterialRevisionCreatePage.tsx
// ADR-074 Rule INV-FE-03: Step 2 of 2 — Create first MaterialRevision
// ADR-075 Rule DU-02/DU-03: stock_uom, production_uom, base_qty اضافه شد
// ADR-095: فیلدهای سفارشی در مرحله دوم (بعد از ایجاد ریویژن) ثبت می‌شوند

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { useV1InventoryMaterialRevisionsCreate, useV1InventoryMaterialRevisionsList } from "@/api/generated/v1/v1";
import { useV1InventoryUomList } from "@/api/generated/v1/v1";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { PageBanner } from "@/shared/components/PageBanner";

type Step = "basic" | "customFields";

export function MaterialRevisionCreatePage() {
  const navigate = useNavigate();
  const { familyId } = useParams<{ familyId: string }>();
  const canCreate = usePermission("inventory.manage");

  // Step management
  const [step, setStep] = useState<Step>("basic");
  const [createdRevisionId, setCreatedRevisionId] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sku, setSku] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [stockUomId, setStockUomId] = useState("");
  const [productionUomId, setProductionUomId] = useState("");
  const [baseNumerator, setBaseNumerator] = useState("");
  const [baseDenominator, setBaseDenominator] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: uomData } = useV1InventoryUomList(undefined, {
    query: {},
  });

  const uoms = uomData?.results ?? [];

  const { data: latestRevisionData } = useV1InventoryMaterialRevisionsList(
    { family: familyId },
    { query: { enabled: !!familyId } }
  );
  
  const latestRevision = latestRevisionData?.results?.[0];

  const { mutate, isPending } = useV1InventoryMaterialRevisionsCreate({
    mutation: {
      onSuccess: (data: any) => {
        const newId = data?.id;
        if (newId) {
          setCreatedRevisionId(newId);
          setStep("customFields");
        } else {
          navigate(`/inventory/materials/${familyId}`);
        }
      },
      onError: (err: any) => {
        setError(
          err?.response?.data?.detail ||
          err?.response?.data?.name?.[0] ||
          "خطا در ایجاد ریویژن"
        );
      },
    },
  });

  if (!canCreate) { navigate("/inventory/materials"); return null; }
  if (!familyId) { navigate("/inventory/materials"); return null; }

  const handleSubmit = () => {
    setError(null);
    if (!name.trim()) { setError("نام متریال الزامی است"); return; }

    const payload: any = {
      family: familyId,
      name: name.trim(),
      description: description.trim(),
      sku: sku.trim(),
      is_active: isActive,
    };

    if (stockUomId) payload.stock_uom = stockUomId;
    if (productionUomId) payload.production_uom = productionUomId;
    if (baseNumerator) payload.base_qty_numerator = parseInt(baseNumerator);
    if (baseDenominator) payload.base_qty_denominator = baseDenominator;

    mutate({ data: payload });
  };

  const inputStyle = {
    width: "100%",
    padding: "0.6rem 0.9rem",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--glass-border)",
    borderRadius: "8px",
    color: "var(--color-text)",
    fontSize: "1rem",
    boxSizing: "border-box" as const,
  };

  const selectStyle = {
    ...inputStyle,
    background: "rgba(30,30,40,0.9)",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "0.4rem",
    fontSize: "0.85rem",
    color: "var(--color-text-muted)",
  };

  if (step === "customFields" && createdRevisionId) {
    return (
      <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        <PageBanner
          title="فیلدهای سفارشی متریال"
          subtitle={`ریویژن «${name}» ایجاد شد — مرحله ۲ از ۲`}
          icon="✨"
          helpLink="/help/inventory/material-revision-create"
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
              <span style={{
                width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "0.75rem", fontWeight: 700,
                background: "rgba(0,200,80,0.2)", color: "#00b050", border: "2px solid #00b050"
              }}>✓</span>
              <div style={{ width: "30px", height: "2px", background: "var(--color-primary)" }} />
              <span style={{
                width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "0.75rem", fontWeight: 700,
                background: "rgba(var(--color-primary-rgb,100,150,255),0.2)", color: "var(--color-primary)",
                border: "2px solid var(--color-primary)"
              }}>2</span>
            </div>
            <span style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: "0.25rem" }}>فیلدهای سفارشی</span>
          </div>
        </PageBanner>

        <div className="brand-card-extand" style={{ maxWidth: "700px", margin: "0 auto", padding: "2rem", width: "100%" }}>

          {/* Success notice */}
          <div style={{
            padding: "0.75rem 1rem", marginBottom: "1.5rem",
            background: "rgba(0,200,80,0.08)", border: "1px solid rgba(0,200,80,0.25)",
            borderRadius: "8px", fontSize: "0.85rem", color: "#00b050"
          }}>
            ✓ اطلاعات پایه ریویژن ذخیره شد. اکنون می‌توانید فیلدهای سفارشی را تکمیل کنید.
          </div>

          {/* Custom Fields */}
          <CustomFieldsPanel
            targetContentType="inventory.materialrevision"
            targetObjectId={createdRevisionId}
            context={{ material_type: "raw_material" }}
            editable={true}
            hideSaveButton={false}
          />

          {/* Navigation */}
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/inventory/materials/${familyId}`)}
            >
              رد کردن و بازگشت
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/inventory/materials/${familyId}`)}
            >
              تکمیل و بازگشت ✓
            </button>
          </div>

          <p style={{ textAlign: "center", fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.75rem" }}>
            فیلدهای سفارشی اختیاری هستند — می‌توانید بعداً از صفحه مشاهده ویرایش کنید
          </p>
        </div>
      </div>
    );
  }

  // ===== STEP 1: Basic Info =====
  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="مشخصات اولیه متریال"
        subtitle="مرحله ۱ از ۲"
        icon="📝"
        helpLink="/help/inventory/material-revision-create"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {/* Step indicator */}
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
            <span style={{
              width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "0.75rem", fontWeight: 700,
              background: "rgba(var(--color-primary-rgb,100,150,255),0.2)", color: "var(--color-primary)",
              border: "2px solid var(--color-primary)"
            }}>1</span>
            <div style={{ width: "30px", height: "2px", background: "var(--glass-border)" }} />
            <span style={{
              width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "0.75rem", fontWeight: 700,
              background: "rgba(128,128,128,0.1)", color: "var(--color-text-muted)",
              border: "2px solid var(--glass-border)"
            }}>2</span>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/inventory/materials")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← انصراف
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "600px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        {/* Context: ایجاد ریویژن جدید */}
        <div className="mb-6 p-3" style={{
          background: "rgba(255,255,255,0.05)", borderRadius: "8px",
          fontSize: "0.85rem", color: "var(--color-text-muted)",
        }}>
          ایجاد ریویژن جدید برای متریال
        </div>

        {error && (
          <div className="mb-4 p-3" style={{
            background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red",
            borderRadius: "8px", color: "red",
          }}>
            {error}
          </div>
        )}

        {latestRevision && (
          <div className="mb-6 p-4" style={{
            background: "rgba(255,255,255,0.03)", borderRadius: "8px",
            border: "1px dashed var(--glass-border)",
            fontSize: "0.9rem"
          }}>
            <h3 style={{ marginBottom: "0.75rem", color: "var(--color-text-muted)", fontSize: "0.95rem" }}>مشخصات آخرین ریویژن:</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", color: "var(--color-text)" }}>
              <div><span style={{ color: "var(--color-text-muted)" }}>نام:</span> <strong>{latestRevision.name}</strong></div>
              <div><span style={{ color: "var(--color-text-muted)" }}>کد فنی:</span> <strong dir="ltr">{latestRevision.sku || "—"}</strong></div>
              <div><span style={{ color: "var(--color-text-muted)" }}>واحد انبار:</span> <strong>{latestRevision.stock_uom_code ?? "—"}</strong></div>
              <div><span style={{ color: "var(--color-text-muted)" }}>واحد تولید:</span> <strong>{latestRevision.production_uom_code ?? "—"}</strong></div>
            </div>
            {latestRevision.description && (
              <div style={{ marginTop: "0.75rem" }}>
                <span style={{ color: "var(--color-text-muted)" }}>توضیحات:</span> {latestRevision.description}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

          {/* نام */}
          <div>
            <label style={labelStyle}>نام متریال <span style={{ color: "red" }}>*</span></label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="مثال: ورق فولادی A36" style={inputStyle} />
          </div>

          {/* توضیحات */}
          <div>
            <label style={labelStyle}>توضیحات</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="توضیحات اختیاری..." rows={2}
              style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          {/* SKU */}
          <div>
            <label style={labelStyle}>کد فنی (SKU)</label>
            <input type="text" value={sku} onChange={(e) => setSku(e.target.value)}
              placeholder="کد فنی قطعه (اختیاری)" dir="ltr" style={inputStyle} />
          </div>

          {/* واحدهای اندازه‌گیری */}
          <div style={{ borderTop: "1px solid var(--glass-border)", paddingTop: "1rem" }}>
            <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
              واحدهای اندازه‌گیری
            </p>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              {/* واحد انبار */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={labelStyle}>واحد انبار (ورود/خروج) <span style={{ color: "red" }}>*</span></label>
                <select value={stockUomId} onChange={(e) => setStockUomId(e.target.value)} style={selectStyle}>
                  <option value="">انتخاب کنید...</option>
                  {uoms.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.symbol || u.code})</option>
                  ))}
                </select>
              </div>

              {/* واحد تولید */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={labelStyle}>واحد تولید (BOM/مصرف)</label>
                <select value={productionUomId} onChange={(e) => setProductionUomId(e.target.value)} style={selectStyle}>
                  <option value="">انتخاب کنید...</option>
                  {uoms.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.symbol || u.code})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ضریب تبدیل پایه */}
          {stockUomId && productionUomId && stockUomId !== productionUomId ? (
            <div style={{
              padding: "1rem",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid var(--glass-border)",
              borderRadius: "8px",
            }}>
              <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
                ضریب تبدیل پایه — فرمول: X واحد تولید = Y واحد انبار
              </p>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "100px" }}>
                  <label style={labelStyle}>
                    X (واحد {uoms.find(u => u.id === productionUomId)?.name ?? "تولید"})
                  </label>
                  <input type="number" value={baseNumerator}
                    onChange={(e) => setBaseNumerator(e.target.value)}
                    placeholder="مثال: 100" min="1" dir="ltr" style={inputStyle} />
                </div>
                <span style={{ color: "var(--color-text-muted)", paddingTop: "1.2rem" }}>=</span>
                <div style={{ flex: 1, minWidth: "100px" }}>
                  <label style={labelStyle}>
                    Y (واحد {uoms.find(u => u.id === stockUomId)?.name ?? "انبار"})
                  </label>
                  <input type="number" value={baseDenominator}
                    onChange={(e) => setBaseDenominator(e.target.value)}
                    placeholder="مثال: 3" min="0" step="0.0001" dir="ltr" style={inputStyle} />
                </div>
              </div>
              {baseNumerator && baseDenominator && (
                <p style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                  یعنی هر واحد {uoms.find(u => u.id === productionUomId)?.name} =&nbsp;
                  {(parseFloat(baseDenominator) / parseInt(baseNumerator)).toFixed(4)}&nbsp;
                  {uoms.find(u => u.id === stockUomId)?.name}
                </p>
              )}
            </div>
          ) : stockUomId && productionUomId && stockUomId === productionUomId ? (
            <div style={{
              padding: "0.75rem 1rem",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "8px",
              border: "1px solid var(--glass-border)",
            }}>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: 0 }}>
                چون واحد انبار و تولید یکسان است، ضریب تبدیل به صورت خودکار ۱:۱ در نظر گرفته می‌شود.
              </p>
            </div>
          ) : null}

          {/* is_active */}
          <div style={{
            display: "flex", alignItems: "center", gap: "0.75rem",
            padding: "0.75rem 1rem",
            background: "rgba(255,255,255,0.03)",
            borderRadius: "8px", border: "1px solid var(--glass-border)",
          }}>
            <input type="checkbox" id="is_active" checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: "1.1rem", height: "1.1rem", cursor: "pointer" }} />
            <label htmlFor="is_active" style={{ cursor: "pointer", fontSize: "0.95rem" }}>
              متریال فعال است
            </label>
          </div>

          {/* Submit → step 2 */}
          <button className="btn btn-primary" onClick={handleSubmit} disabled={isPending}
            style={{ width: "100%", padding: "0.85rem", fontSize: "1rem" }}>
            {isPending ? "در حال ذخیره..." : "بعدی: فیلدهای سفارشی ←"}
          </button>

          <p style={{ textAlign: "center", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
            ریویژن به‌عنوان DRAFT ذخیره می‌شود — برای فعال‌سازی باید Publish شود
          </p>
        </div>
      </div>
    </div>
  );
}